import { Formik, FormikValues } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { FormQuestion, FormQuestionType } from '../../../api/types/forms';
import { MinimalFormQuestionSchema } from '../../../api/forms/parser';
import { CreateFormQuestionDto } from '../../../api/typeDtos/forms';
import { createFormQuestion, updateFormQuestion } from '../../../api/forms';
import ProjectTextArea from '../../inputs/ProjectTextArea';
import { getFormResponseTypeAndKeys } from '../../../api/forms/utils';
import { getDisplayNames } from '../../../api/utils';
import ProjectPicker from '../../ProjectPicker';
import { AppSettingsContext } from '../../../providers/AppSettingsProvider/context';
import useCustomizedDropdown from '../../../hooks/useCustomizedDropdown';
import ProjectSearchDropdown from '../../ProjectSearchDropdown';

type FormQuestionInitialValues = {
  key: string;
  type: string;
  text: string;
  presentationText: string;
  quoteText: string;
  quoteAuthor: string;
  pillar: string;
  firstResponseDescription: string;
  firstResponsePlaceholder: string;
  secondResponseDescription: string;
  secondResponsePlaceholder: string;
};

const ReflectionFormComponent = ({
  onCancel,
  initialValues,
  onSubmit,
}: {
  onCancel: () => void;
  initialValues: FormQuestionInitialValues;
  onSubmit: (values: FormikValues) => Promise<void>;
}) => {
  return (
    <Formik initialValues={initialValues} validationSchema={MinimalFormQuestionSchema} onSubmit={onSubmit}>
      {({ values, handleChange, errors, touched }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldLabelBigger>Question details</Components.FieldLabelBigger>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Text</Components.FieldLabel>
                  <ProjectTextArea
                    value={values?.text}
                    onChangeText={handleChange}
                    name={'text'}
                    errorMessage={errors.text && touched.text ? errors?.text : undefined}
                    placeholder={'Enter text (the main question)'}
                    rows={2}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'readyToPlay'} />
              <Components.FieldWrapper>
                <Components.FieldLabel>Quote text</Components.FieldLabel>
                <ProjectTextArea
                  value={values?.quoteText}
                  onChangeText={handleChange}
                  name={'quoteText'}
                  errorMessage={errors?.quoteText && touched?.quoteText ? errors?.quoteText : undefined}
                  placeholder={'Enter the quote text'}
                  rows={2}
                />
              </Components.FieldWrapper>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Presentation text</Components.FieldLabel>
                  <ProjectTextArea
                    value={values?.presentationText}
                    onChangeText={handleChange}
                    name={'presentationText'}
                    errorMessage={
                      errors.presentationText && touched.presentationText ? errors?.presentationText : undefined
                    }
                    placeholder={'Enter presentation text (alternative text, shown in the journal tab)'}
                    rows={2}
                  />
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Quote author</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.quoteAuthor}
                    onChangeText={handleChange}
                    name={'quoteAuthor'}
                    errorMessage={errors?.quoteAuthor && touched?.quoteAuthor ? errors?.quoteAuthor : undefined}
                    placeholder={"Enter the quote's author (if empty, the author field will be hidden in-app)"}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldLabelBigger>Response details</Components.FieldLabelBigger>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.PrefixedTextFieldWrapper>
                  <Components.PrefixLabel>Description</Components.PrefixLabel>
                  <ProjectTextField
                    value={values?.firstResponseDescription}
                    onChangeText={handleChange}
                    name={'firstResponseDescription'}
                    errorMessage={
                      errors.firstResponseDescription && touched.firstResponseDescription
                        ? errors?.firstResponseDescription
                        : undefined
                    }
                    placeholder={'Enter text'}
                  />
                </Components.PrefixedTextFieldWrapper>
                <Components.PrefixedTextFieldWrapper>
                  <Components.PrefixLabel>Placeholder</Components.PrefixLabel>
                  <ProjectTextField
                    value={values?.firstResponsePlaceholder}
                    onChangeText={handleChange}
                    name={'firstResponsePlaceholder'}
                    errorMessage={
                      errors.firstResponsePlaceholder && touched.firstResponsePlaceholder
                        ? errors?.firstResponsePlaceholder
                        : undefined
                    }
                    placeholder={'Enter text'}
                  />
                </Components.PrefixedTextFieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const JournalQuestionFormComponent = ({
  onCancel,
  initialValues,
  onSubmit,
  responsesNumber,
  setResponsesNumber,
}: {
  onCancel: () => void;
  initialValues: FormQuestionInitialValues;
  onSubmit: (values: FormikValues) => Promise<void>;
  responsesNumber: number;
  setResponsesNumber: React.Dispatch<React.SetStateAction<number>>;
}) => {
  return (
    <Formik initialValues={initialValues} validationSchema={MinimalFormQuestionSchema} onSubmit={onSubmit}>
      {({ values, handleChange, errors, touched }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldLabelBigger>Question details</Components.FieldLabelBigger>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Text</Components.FieldLabel>
                  <ProjectTextArea
                    value={values?.text}
                    onChangeText={handleChange}
                    name={'text'}
                    errorMessage={errors.text && touched.text ? errors?.text : undefined}
                    placeholder={'Enter text (the main question)'}
                    rows={2}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Presentation text</Components.FieldLabel>
                  <ProjectTextArea
                    value={values?.presentationText}
                    onChangeText={handleChange}
                    name={'presentationText'}
                    errorMessage={
                      errors.presentationText && touched.presentationText ? errors?.presentationText : undefined
                    }
                    placeholder={'Enter presentation text (alternative text, shown in the journal tab)'}
                    rows={2}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldLabelBigger>Response details</Components.FieldLabelBigger>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.CheckboxFieldWrapper>
                  <Components.CheckboxLabel>Question has two response fields</Components.CheckboxLabel>
                  <Components.Checkbox
                    checked={responsesNumber === 2}
                    value={responsesNumber}
                    onChange={(e) => {
                      setResponsesNumber(e?.target?.value === '1' ? 2 : 1);
                    }}
                  />
                </Components.CheckboxFieldWrapper>

                <Components.FieldLabel>
                  {responsesNumber === 2 ? 'First response field' : 'Single response field'}
                </Components.FieldLabel>
                <Components.PrefixedTextFieldWrapper>
                  <Components.PrefixLabel>Description</Components.PrefixLabel>
                  <ProjectTextField
                    value={values?.firstResponseDescription}
                    onChangeText={handleChange}
                    name={'firstResponseDescription'}
                    errorMessage={
                      errors.firstResponseDescription && touched.firstResponseDescription
                        ? errors?.firstResponseDescription
                        : undefined
                    }
                    placeholder={'Enter text'}
                  />
                </Components.PrefixedTextFieldWrapper>
                <Components.PrefixedTextFieldWrapper>
                  <Components.PrefixLabel>Placeholder</Components.PrefixLabel>
                  <ProjectTextField
                    value={values?.firstResponsePlaceholder}
                    onChangeText={handleChange}
                    name={'firstResponsePlaceholder'}
                    errorMessage={
                      errors.firstResponsePlaceholder && touched.firstResponsePlaceholder
                        ? errors?.firstResponsePlaceholder
                        : undefined
                    }
                    placeholder={'Enter text'}
                  />
                </Components.PrefixedTextFieldWrapper>

                {responsesNumber === 2 && (
                  <>
                    <Components.FieldLabel>Second response field</Components.FieldLabel>
                    <Components.PrefixedTextFieldWrapper>
                      <Components.PrefixLabel>Description</Components.PrefixLabel>
                      <ProjectTextField
                        value={values?.secondResponseDescription}
                        onChangeText={handleChange}
                        name={'secondResponseDescription'}
                        errorMessage={
                          errors.secondResponseDescription && touched.secondResponseDescription
                            ? errors?.secondResponseDescription
                            : undefined
                        }
                        placeholder={'Enter text'}
                      />
                    </Components.PrefixedTextFieldWrapper>
                    <Components.PrefixedTextFieldWrapper>
                      <Components.PrefixLabel>Placeholder</Components.PrefixLabel>
                      <ProjectTextField
                        value={values?.secondResponsePlaceholder}
                        onChangeText={handleChange}
                        name={'secondResponsePlaceholder'}
                        errorMessage={
                          errors.secondResponsePlaceholder && touched.secondResponsePlaceholder
                            ? errors?.secondResponsePlaceholder
                            : undefined
                        }
                        placeholder={'Enter text'}
                      />
                    </Components.PrefixedTextFieldWrapper>
                  </>
                )}
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const PillarSurveyQuestionFormComponent = ({
  onCancel,
  initialValues,
  onSubmit,
  pillarTypeProps,
  setPillarTypeProps,
  existingQuestionKeys,
}: {
  onCancel: () => void;
  initialValues: FormQuestionInitialValues;
  onSubmit: (values: FormikValues) => Promise<void>;
  pillarTypeProps: any;
  setPillarTypeProps: any;
  existingQuestionKeys?: string[];
}) => {
  const [formErrors, setFormErrors] = useState<{ key: string | null; text: string | null; pillar: string | null }>({
    key: null,
    text: null,
    pillar: null,
  });

  useEffect(() => {
    if (initialValues.pillar) {
      setPillarTypeProps([initialValues.pillar]);
    }
  }, [initialValues]);

  const existingKeyError = 'A question with this key already exists. Please try again with a different one.';
  const missingKeyError = 'Question key is required';
  const validateCustomFields = async (values: FormikValues): Promise<boolean> => {
    let keyError = null;
    let textError = null;
    let pillarError = null;
    let formValid = true;
    if (!values?.key || values?.key === '') {
      keyError = missingKeyError;
      formValid = false;
    }
    if (existingQuestionKeys?.includes(values?.key) && values?.key !== initialValues?.key) {
      keyError = existingKeyError;
      formValid = false;
    }
    if (!values?.text || values?.text === '') {
      textError = 'Question text is required';
      formValid = false;
    }
    if (pillarTypeProps?.selectedItems?.length === 0) {
      pillarError = 'Pillar is required';
      formValid = false;
    }
    setFormErrors({ key: keyError, text: textError, pillar: pillarError });
    return formValid;
  };

  const _onSubmit = async (values: FormikValues) => {
    const formValid = await validateCustomFields(values);
    if (formValid) {
      await onSubmit(values);
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={_onSubmit}>
      {({ values, handleChange }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldLabelBigger>Question details</Components.FieldLabelBigger>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Key</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.key}
                    onChangeText={handleChange}
                    name={'key'}
                    placeholder={'Enter key'}
                  />
                  {((formErrors.key === missingKeyError && !values?.key?.length) ||
                    formErrors.key === existingKeyError) && (
                    <Components.ErrorMessage>{`${formErrors.key}`}</Components.ErrorMessage>
                  )}
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Text</Components.FieldLabel>
                  <ProjectTextArea
                    value={values?.text}
                    onChangeText={handleChange}
                    name={'text'}
                    placeholder={'Enter text (the main question)'}
                    rows={2}
                  />
                  {formErrors.text && !values?.text?.length && (
                    <Components.ErrorMessage>{`${formErrors.text}`}</Components.ErrorMessage>
                  )}
                </Components.FieldWrapper>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Pillar</Components.FieldLabel>
                  <ProjectPicker dropdownProps={pillarTypeProps} />
                  <ProjectSearchDropdown {...pillarTypeProps} />
                  {formErrors.pillar && !pillarTypeProps?.selectedItems?.length && (
                    <Components.ErrorMessage>{`${formErrors.pillar}`}</Components.ErrorMessage>
                  )}
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
  existingQuestionKeys,
}: {
  modalState?: { formQuestion?: FormQuestion; questionType?: FormQuestionType };
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
  existingQuestionKeys?: string[];
}) => {
  const formQuestion = modalState?.formQuestion;
  const formQuestionType = modalState?.questionType;

  const initialValues = {
    key: formQuestion?.key || '',
    type: formQuestion?.type || formQuestionType || '',
    text: formQuestion?.text || '',
    presentationText: formQuestion?.presentationText || '',
    quoteText: formQuestion?.extraData?.quoteText || '',
    quoteAuthor: formQuestion?.extraData?.quoteAuthor || '',
    pillar: formQuestion?.extraData?.pillar || '',
    firstResponseDescription: formQuestion?.responses?.[0]?.description || '',
    firstResponsePlaceholder: formQuestion?.responses?.[0]?.placeholder || '',
    secondResponseDescription: formQuestion?.responses?.[1]?.description || '',
    secondResponsePlaceholder: formQuestion?.responses?.[1]?.placeholder || '',
  };

  const { pillarTypes } = useContext(AppSettingsContext);

  const { props: pillarTypeProps, setProps: setPillarTypeProps } = useCustomizedDropdown({
    possibleValues: pillarTypes,
    singleSelect: true,
    customType: 'pillarType',
  });

  const [responsesNumber, setResponsesNumber] = useState(formQuestion?.responses?.length || 1);

  const onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
      responsesNumber: responsesNumber,
      pillar: pillarTypeProps.selectedItems?.[0]?.value,
    });
  };

  return formQuestionType === 'reflection' ? (
    <ReflectionFormComponent initialValues={initialValues} onCancel={onCancel} onSubmit={onSubmit} />
  ) : formQuestionType === 'journal' ? (
    <JournalQuestionFormComponent
      initialValues={initialValues}
      onCancel={onCancel}
      onSubmit={onSubmit}
      responsesNumber={responsesNumber}
      setResponsesNumber={setResponsesNumber}
    />
  ) : formQuestionType === 'pillarSurvey' ? (
    <PillarSurveyQuestionFormComponent
      initialValues={initialValues}
      onCancel={onCancel}
      onSubmit={onSubmit}
      pillarTypeProps={pillarTypeProps}
      setPillarTypeProps={setPillarTypeProps}
      existingQuestionKeys={existingQuestionKeys}
    />
  ) : null;
};

const CreateOrEditFormQuestionModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
  existingQuestionKeys,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { formQuestion?: FormQuestion; questionType?: FormQuestionType };
  refreshCallback?: () => Promise<void>;
  existingQuestionKeys?: string[];
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const questionKey = values?.key ? values?.key : undefined;
    const questionType = values?.type;
    const responsesNumber = values?.responsesNumber || 1;
    const responsesMinimalData = getFormResponseTypeAndKeys(questionType, responsesNumber, questionKey);
    const _responses = [
      {
        ...responsesMinimalData[0],
        description: values?.firstResponseDescription?.length ? values?.firstResponseDescription : null,
        placeholder: values?.firstResponsePlaceholder?.length ? values?.firstResponsePlaceholder : null,
      },
    ];
    if (responsesNumber === 2) {
      _responses.push({
        ...responsesMinimalData[1],
        description: values?.secondResponseDescription?.length ? values?.secondResponseDescription : null,
        placeholder: values?.secondResponsePlaceholder?.length ? values?.secondResponsePlaceholder : null,
      });
    }
    const data: CreateFormQuestionDto = {
      ...(questionKey ? { key: questionKey } : {}),
      type: questionType,
      text: values?.text,
      presentationText: values?.presentationText?.length ? values?.presentationText : null,
      extraData: {
        quoteText: values?.quoteText?.length ? values?.quoteText : null,
        quoteAuthor: values?.quoteAuthor?.length ? values?.quoteAuthor : null,
        pillar: values?.pillar?.length ? values?.pillar : null,
      },
      responses: _responses,
    };

    setIsLoading(true);
    const result =
      modalState && modalState?.formQuestion?.id
        ? await updateFormQuestion({ ...data, id: modalState?.formQuestion?.id })
        : await createFormQuestion(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.formQuestion?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a question');
      onModalClose?.(true);
      refreshCallback?.().then();
    }
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={
          <ModalBody
            modalState={modalState}
            onSave={_onSave}
            onCancel={_onModalClose}
            existingQuestionKeys={existingQuestionKeys}
          />
        }
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={
          (modalState?.formQuestion ? 'Edit ' : 'Create new ') +
          getDisplayNames.formQuestionType(modalState?.questionType)?.toLowerCase() +
          (modalState?.questionType === 'reflection' ? '' : ' question')
        }
        titleSuffix={modalState?.formQuestion ? `(ID: ${modalState?.formQuestion?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditFormQuestionModal;
